import { TicketFormSummary } from '@Components/ticket-list/ticket-form-summary/ticket-form-summary'
import './styles.scss'
import { pollingRequest, RootState, useAppDispatch } from '@AppState/redux-store'
import { useSelector } from 'react-redux'
import { useCallback, useEffect, useMemo } from 'react'
import { fetchScoredTicketHistory, fetchScoredTicketResults } from '@AppState/ticket-history'
import { Utils } from '@Util/utils'
import {
  mapAddonDetails,
  mapListResults,
  mapTicketToTicketInfoForm,
} from '@Components/ticket-list/ticket-form-summary/utils'
import { MatchStatus } from '@Components/ticket-list/number-selection-summary/number-selection-summary-types'
import { AddOnTypes } from '@Config/types'
import { DataFetchingState } from '@AppState/types'
import { TicketResult } from '@API/game-results/types'
import { SavedKenoTicket } from '@interaction-gaming/player-data-service-browser-client'
import { HostConfig } from '@AppState/host-config'
import { NavigationActions } from '@AppState/navigation'
import { NAV_VALUES } from '@Components/navigation-bar/types'
import {
  fetchTicketResults,
  NAV_VALUES as TICKET_NAV_VALUES,
  TicketModeActions,
} from '@AppState/ticket-info'
import { PollingActions } from '@AppState/polling/polling-slice'

const TICKET_HISTORY_POLLING_ID = 'fetchTicketHistoryResult'
const CLASS_NAME = 'gby-k2g-ticket-history-mode'

export const CardStack = ({
  children,
  numberOfCards,
}: {
  children: React.ReactNode
  numberOfCards: number
}) => {
  const cards = new Array(numberOfCards - 1).fill(null).map((_, i) => {
    return (
      <div
        key={i}
        className={`${CLASS_NAME}__card`}
        style={{ '--card-order': i } as React.CSSProperties}></div>
    )
  })

  return (
    <div className={`${CLASS_NAME}__card-stack`}>
      <div className={`${CLASS_NAME}__top-card`}>{children}</div>
      {cards}
    </div>
  )
}

export const TicketHistoryMode = () => {
  const ticketHistoryEnabled = useSelector(
    (state: RootState) => state.hostConfig.config?.features?.ticketHistory,
  )
  const hostConfig = useSelector((state: RootState) => state.hostConfig.config)
  const pollingIntervalMap = useSelector((state: RootState) => state.polling.intervalMap)

  const { savedKenoTickets: savedTickets, fetchState: fetchScoredTicketHistoryState } = useSelector(
    (state: RootState) => state.ticketHistoryMode,
  )
  const kenoTicketResults = useSelector(
    (state: RootState) => state.ticketHistoryMode.kenoTicketResults,
  )
  const latestDraw = useSelector((state: RootState) => state.latestDraw.latestDraw)

  const dispatch = useAppDispatch()

  useEffect(() => {
    document.documentElement.style.setProperty('--app-background-color', `#E5E5E5`)
    document.documentElement.style.setProperty('--app-background-glow-color', `#E5E5E5`)
  }, [])

  useEffect(() => {
    if (fetchScoredTicketHistoryState !== DataFetchingState.SUCCEEDED) {
      dispatch(fetchScoredTicketHistory())
    }
  }, [dispatch, fetchScoredTicketHistoryState])

  useEffect(() => {
    const pendingTicketIds: string[] = Object.keys(kenoTicketResults).filter(
      (id) => id && kenoTicketResults[id] && !kenoTicketResults[id].ticketComplete,
    )

    const completeTicketIds: string[] = Object.keys(kenoTicketResults).filter(
      (id) => id && kenoTicketResults[id] && kenoTicketResults[id].ticketComplete,
    )

    pendingTicketIds.forEach((id) => {
      const pollingId = `${TICKET_HISTORY_POLLING_ID}:${id}`
      if (!pollingIntervalMap[pollingId]) {
        dispatch(
          pollingRequest(
            fetchScoredTicketResults(savedTickets.find((t) => t.id === id)!),
            pollingId,
            60000,
          ),
        )
      }
    })

    completeTicketIds.forEach((id) => {
      const pollingId = `${TICKET_HISTORY_POLLING_ID}:${id}`
      if (pollingIntervalMap[pollingId]) {
        dispatch(PollingActions.clearPollingInterval(pollingId))
      }
    })
  }, [dispatch, pollingIntervalMap, kenoTicketResults, savedTickets])

  useEffect(() => {
    return () => {
      savedTickets.forEach((ticket) => {
        const pollingId = `${TICKET_HISTORY_POLLING_ID}:${ticket.id}`
        if (pollingId && pollingIntervalMap[pollingId]) {
          dispatch(PollingActions.clearPollingInterval(pollingId))
        }
      })
    }
    // We only want this to run on component unmount - adding anything in the array causes it to be called on rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const navigateToTicketMode = useCallback(
    (action: TICKET_NAV_VALUES, savedKenoTicket: SavedKenoTicket, newTicket: boolean = false) => {
      const ticketFormInfo = mapTicketToTicketInfoForm(savedKenoTicket, newTicket, latestDraw)
      dispatch(TicketModeActions.updateTicketForm(ticketFormInfo))
      dispatch(fetchTicketResults(ticketFormInfo))
      dispatch(TicketModeActions.updateActiveMenu(action))
      dispatch(NavigationActions.updateActiveMode(NAV_VALUES.TICKET_MODE))
    },
    [dispatch],
  )

  const mapTicketToSummary = useCallback(
    (ticketResult: TicketResult, savedKenoTicket: SavedKenoTicket, hostConfig: HostConfig) => {
      const sortedDrawNumberDrawResults = [...ticketResult.scoredDrawResults].sort(
        (a, b) => a.drawingNumber - b.drawingNumber,
      )
      // Sort pending results before non pending ones, then by drawing number
      const sortedPendingDrawResults = [...ticketResult.scoredDrawResults].sort((a, b) => {
        if (a.drawingComplete && !b.drawingComplete) {
          return 1
        } else if (!a.drawingComplete && b.drawingComplete) {
          return -1
        } else {
          return a.drawingNumber - b.drawingNumber
        }
      })

      const totalWinnings = sortedPendingDrawResults.reduce(
        (acc, result) => acc + (result.drawingComplete ? result.totalWinningsInCents : 0),
        0,
      )

      const latestDrawing = mapListResults(
        [sortedPendingDrawResults[0]],
        savedKenoTicket.numberSelections,
        savedKenoTicket.addonSelection.extraDrawAddon,
        hostConfig,
      )[0]

      const addonDetails = mapAddonDetails(
        savedKenoTicket.addonSelection.multiplierAddon
          ? { name: savedKenoTicket.addonSelection.multiplierAddon, type: AddOnTypes.MULTIPLIER }
          : undefined,
        savedKenoTicket.addonSelection.extraDrawAddon
          ? { name: savedKenoTicket.addonSelection.extraDrawAddon, type: AddOnTypes.EXTRA_DRAWS }
          : undefined,
        savedKenoTicket.addonSelection.miniGameAddon
          ? { name: savedKenoTicket.addonSelection.miniGameAddon.name, type: AddOnTypes.MINI_GAME }
          : undefined,
        hostConfig.addOns.map((addOn) => {
          return { name: addOn.name, color: addOn.listResultColor }
        }),
      )

      const numberSelections = latestDrawing.numberSelections.map((ns) => ({
        ...ns,
        addOnName: null,
        matchStatus: MatchStatus.UNKNOWN,
      }))

      const firstDrawNumber = sortedDrawNumberDrawResults[0].drawingNumber
      const lastDrawNumber =
        sortedDrawNumberDrawResults[sortedDrawNumberDrawResults.length - 1].drawingNumber

      return (
        <CardStack
          key={savedKenoTicket.id}
          numberOfCards={
            sortedPendingDrawResults.length >= 3 ? 3 : sortedPendingDrawResults.length === 2 ? 2 : 1
          }>
          <TicketFormSummary
            addOnDetails={addonDetails}
            drawDate={null}
            drawNumber={firstDrawNumber}
            endingDrawNumber={firstDrawNumber !== lastDrawNumber ? lastDrawNumber : undefined}
            numbersPerRow={5}
            numberSelections={numberSelections}
            iconUrl={latestDrawing.iconUrl}
            onClick={Utils.noop}
            totalWinnings={totalWinnings}
            type={latestDrawing.type}
            isTicketHistorySummary={true}
            onTicketActionClick={(action: TICKET_NAV_VALUES, newTicket?: boolean) => {
              navigateToTicketMode(action, savedKenoTicket, newTicket)
            }}
            betAmountInCents={savedKenoTicket.wagerInCents}
          />
        </CardStack>
      )
    },
    [navigateToTicketMode],
  )

  const ticketList = useMemo(() => {
    return savedTickets.map((ticket) => {
      const result = kenoTicketResults[ticket.id]
      if (!result || !hostConfig) return null

      return mapTicketToSummary(result, ticket, hostConfig)
    })
  }, [hostConfig, kenoTicketResults, savedTickets, mapTicketToSummary])

  if (!ticketHistoryEnabled) {
    return null
  }

  return (
    <div className={CLASS_NAME}>
      <div className={`${CLASS_NAME}__content-title`}>Ticket History</div>
      <div className={`${CLASS_NAME}__content`}>{ticketList}</div>
    </div>
  )
}
